import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {WidgetData} from "../../../../../core/shared/widget/widget-data";
import {WidgetDataParam} from "../../../../../core/shared/widget/widget-data-param";
import {NavigationItem} from "../../../../../core/shared/dto/navigation/navigation-item";
import {WidgetConfigInterface} from "../../../../../core/shared/mvs-page/interface/widget-config-page.interface";
import {ConfigPageComponent} from "../../../../../core/shared/mvs-page/config-page.component";
import {PageHeaderConfig} from "../../../../../core/shared/service/page/page-header-config.interface";
import {MvsConfigHeaderComponent} from "../../../../../core/shared/components/mvs-config-header/mvs-config-header.component";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsObjectNavigationService} from "../../../../../core/shared/navigation/mvs-object-navigation.service";
import {MvsObjectNavigationEntry} from "../../../../../core/shared/navigation/mvs-object-navigation-entry";
import {MvsObjectNavigationActionEnum} from "../../../../../core/shared/navigation/mvs-object-navigation-action-enum";
import {WidgetFactory} from "../../../../../core/shared/widget/service/widget.factory";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {ObjectIdentifier} from "../../../../../core/shared/basic/object-identifier";
import {ObjectIdentifierData} from "../../../../../core/shared/basic/object-identifier-data";
import {WidgetFunctionCallBackCreate, WidgetToolbarCreateInterface} from "../../../../../core/shared/helper/widget-function-call-back-create";

@Component({
    selector: 'dc-config-page',
    templateUrl: './dc-config.page.html',
    styleUrls: ['./dc-config.page.scss'],
    standalone: false
})
export class DcConfigPage extends ConfigPageComponent implements OnInit {

    defaultLabel: string = 'Dokumentenklassifizierung Konfiguration';

    navigationItems: NavigationItem[] = [
        {
            label: 'Seitentypen',
            action: 'dc.DcPageType',
            tooltip: 'Seitentypen und Abschnitte',
            icon: 'fa-solid fa-folder-tree',
            toggleable: false,
            default: true,
        },
        {
            label: 'Seiten',
            action: 'dc.DcPage',
            tooltip: 'Seiten mit Inhalten, Objekten und Begriffen',
            icon: 'fa-solid fa-file-lines',
            toggleable: false,
        },
    ];

    override widgets: { [key: string]: WidgetConfigInterface } = {
        widgetDcPageType: {
            alias: 'dc.config.page.type.selectable.new',
            label: 'Seitentypen',
            entity: 'dc.DcPageType',
            widgetData: new WidgetData(),
            postFunction: (widget: WidgetConfigInterface) => {
                widget.widgetData.uiComponent = 'selectable';
                widget.widgetData.setParam(WidgetDataParam.create('attributeToDisplay', 'e.name'));
                widget.widgetData.setParam(WidgetDataParam.create('showSearch', true));
            }
        },
        widgetDcPage: {
            alias: 'dc.config.page.table.new',
            label: 'Seiten',
            entity: 'dc.DcPage',
            widgetData: new WidgetData(),
        },
    };

    // Nav 1 — DcPageType children (sidebar + inner tabs, kept intentionally)
    selectedDcPageTypeId: number;
    dcPageChildWidget: WidgetData;
    dcPageTypeSectionChildWidget: WidgetData;
    dcPageTypeInnerTabIndex: number = 0;

    constructor(
        protected route: ActivatedRoute,
        protected coreService: MvsCoreService,
        protected navigationService: MvsObjectNavigationService,
        protected router: Router,
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.refreshDcPageTypeChildrenAll();
        this.initialized = true;
    }

    override postNavigationSelection(): void {
        // CONFIG-020 rule 3: clear stale state when leaving the nav item that owns it.
        // Without this, returning to dc.DcPageType re-applies the filter while the
        // selectable sidebar cannot restore its visual selection → inconsistent UI.
        // Reset selected ID, null child WidgetData (forces lazy re-init), strip URL param.
        if (this.activeNavigationItem?.action !== 'dc.DcPageType') {
            this.selectedDcPageTypeId = undefined;
            this.dcPageChildWidget = null;
            this.dcPageTypeSectionChildWidget = null;
            this.dcPageTypeInnerTabIndex = 0;

            if (this.route.snapshot.queryParams['pageTypeId']) {
                this.router.navigate([], {
                    queryParams: {pageTypeId: null},
                    queryParamsHandling: 'merge',
                    replaceUrl: true,
                });
            }
            return;
        }

        if (this.activeNavigationItem?.action === 'dc.DcPageType') {
            const pageTypeId = this.route.snapshot.queryParams['pageTypeId'];
            if (pageTypeId) {
                this.selectedDcPageTypeId = +pageTypeId;
                this.refreshDcPageTypeChildren(+pageTypeId);
            } else {
                this.selectedDcPageTypeId = undefined;
                this.refreshDcPageTypeChildrenAll();
            }
        }
    }

    // ── DcPageType → DcPage + DcPageTypeSection ──────────────────────────────

    refreshDcPageTypeChildrenAll(): void {
        this.dcPageChildWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'dc.config.page.type.children.page', 'Alle Seiten', 'dc.DcPage', []
        );
        this.dcPageTypeSectionChildWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'dc.config.page.type.children.section', 'Alle Abschnitte', 'dc.DcPageTypeSection', []
        );
    }

    private refreshDcPageTypeChildren(pageTypeId: number): void {
        const filter = FilterCriteria.create('e.pageTypeId', FilterCriteria.cOperatorEqual, pageTypeId);
        const fkFields: WidgetToolbarCreateInterface[] = [{fieldName: 'pageTypeDtoId', fieldValue: pageTypeId}];

        this.dcPageChildWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'dc.config.page.type.children.page', 'Seiten', 'dc.DcPage', [filter]
        );
        this.dcPageChildWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fkFields);

        this.dcPageTypeSectionChildWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'dc.config.page.type.children.section', 'Abschnitte', 'dc.DcPageTypeSection', [filter]
        );
        this.dcPageTypeSectionChildWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(fkFields);
    }

    onDcPageTypeSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const id = (event as ObjectIdentifierData).data?.['e.id'] ?? event.objectId;
        this.selectedDcPageTypeId = id;
        this.dcPageTypeInnerTabIndex = 0;
        this.refreshDcPageTypeChildren(id);
        this.router.navigate([], {queryParams: {pageTypeId: id}, queryParamsHandling: 'merge', replaceUrl: true});
    }

    onDcPageTypeDeselect(): void {
        this.selectedDcPageTypeId = undefined;
        this.refreshDcPageTypeChildrenAll();
        this.router.navigate([], {queryParams: {pageTypeId: null}, queryParamsHandling: 'merge', replaceUrl: true});
    }

    // ── Shared ────────────────────────────────────────────────────────────────

    /**
     * Opens the entity in its registered Object Page (navigationService resolves
     * the objectComponent registered for MvsCrudModeEnum.update in the entity
     * provider — e.g. DcPageObjectComponent for dc.DcPage).
     */
    handleEntitySelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        this.navigationService.navigateTo(
            MvsObjectNavigationEntry.createNavigationEntry(
                event.objectType, event.objectId, 'object', '', '', '',
                MvsObjectNavigationActionEnum.edit
            )
        );
    }

    getPageHeaderConfig(): PageHeaderConfig {
        return {
            component: MvsConfigHeaderComponent,
            inputs: {
                header: 'Dokumentenklassifizierung Konfiguration',
                icon: 'fa-solid fa-file-magnifying-glass'
            }
        };
    }

    isNavComponent(): NavigationItem[] {
        return this.navigationItems;
    }

}
